/* eslint-disable import/extensions */
import { useIsMutating, useMutation } from 'react-query';
import { useSelector } from 'react-redux';
import { captureException } from '@sentry/react';
import { URL_API } from '../constants';
import { chatgenieAPI } from '../services/chatgenieAPI';
import useToast from './useToast';
import mutationKeys from '../constants/mutationKeys';
import { store as reduxStore } from '../redux/store';

export const executeAction = ({
  actions,
  form_data,
  onSuccess,
  ticketId,
  companyId,
  channelId,
  userId
}) =>
  chatgenieAPI
    .instance({
      method: 'POST',
      url: `${URL_API}/api/action-platform/actions`,
      data: {
        type: 'button',
        user_id: userId,
        entity_type: 'ticket',
        entity_id: ticketId,
        company_id: companyId,
        channel_id: channelId,
        action: {
          id: 'action_platform_command',
          selected_option: actions
        },
        form_data
      }
    })
    .then(res => {
      if (onSuccess) onSuccess(res.data, { actions, form_data });

      return res.data;
    });

export const getMemberOptions = async ({ query, ticket }) => {
  const { userId } = reduxStore.getState().users.user;
  const { options } = await executeAction({
    actions: { value: 'action_platform/connectwise/getMembers' },
    form_data: { query },
    ticketId: ticket.id,
    companyId: ticket.company?.id || ticket.company_id,
    channelId: ticket.conversation_channel?.id,
    userId
  });
  return options;
};

export const assignMemberWithActionData = async ({ memberActionData, ticket }) => {
  const { userId } = reduxStore.getState().users.user;
  const res = await executeAction({
    ...memberActionData,
    ticketId: ticket.id,
    companyId: ticket.company?.id || ticket.company_id,
    channelId: ticket.conversation_channel?.id,
    userId
  });
  return res;
};

const useAction = ({ ticket, actionKey } = {}) => {
  const { userId } = useSelector(store => store.users.user);
  const { toast } = useToast();

  const numberOfMutations = useIsMutating([mutationKeys.THREAD_ACTION, ticket.id, actionKey]);
  const isMutating = !!numberOfMutations;

  const mutation = useMutation(
    [mutationKeys.THREAD_ACTION, ticket.id, actionKey],
    (payload, { onSuccess } = {}) =>
      executeAction({
        ...payload,
        onSuccess,
        userId,
        ticketId: ticket.id,
        companyId: ticket.company?.id || ticket.company_id,
        channelId: ticket.conversation_channel?.id
      }),
    {
      onError: err => {
        const message = err?.response?.data?.message || err.message;
        toast.error(message);
        captureException(new Error(message));
      }
    }
  );

  return {
    isLoadingMutation: mutation.isLoading || isMutating,
    sendAction: mutation.mutateAsync
  };
};

export default useAction;
